import React, { useMemo, useState } from "react";
import PhotoAlbum from "react-photo-album";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/styles.css";
import { Switch, Route, useRouteMatch } from "react-router-dom";
import { Button, Stack } from "react-bootstrap";
import { Helmet } from "react-helmet";

const CultureGallery = ({ culture }) => {
  const [index, setIndex] = useState(-1);

  const photos = culture.photos.slice().reverse();

  const sizes = {
    size: "960px",
    // These values are set based on the bootstrap Container defaults.
    // size corresponds to the width we have for the photo gallery (the below div)
    // at different viewport width.
    sizes: [
      { viewport: "(max-width: 576px)", size: "540px" },
      { viewport: "(max-width: 768px)", size: "720px" },
      { viewport: "(max-width: 992px)", size: "960px" },
      { viewport: "(max-width: 1200px)", size: "1140px" },
      { viewport: "(max-width: 1400px)", size: "1320px" },
    ],
  };

  return (
    <div>
      <PhotoAlbum
        photos={photos}
        sizes={sizes}
        layout="rows"
        onClick={({ index }) => setIndex(index)}
      />

      <Lightbox
        slides={photos}
        open={index >= 0}
        index={index}
        close={() => setIndex(-1)}
        // enable optional lightbox plugins
        plugins={[Captions]}
      />
    </div>
  );
};

const copyToClipboard = async (text) => {
  await navigator.clipboard.writeText(text);
};

const AddEditCulture = ({ culture = { photos: [] } }) => {
  const [outputJson, setOutputJson] = useState(null);
  const [agree, setAgree] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedKey, setSelectedKey] = useState(""); // src
  const [prefill, setPrefill] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const options = useMemo(() => {
    return (culture?.photos || [])
      .map((p) => ({ key: p.src, label: p.description ? `${p.description} — ${p.src}` : p.src, photo: p }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [culture]);

  function switchMode(next) {
    setMode(next);
    if (next === "add") {
      setPrefill(null);
      setSelectedKey("");
      setFormKey((k) => k + 1);
    }
  }

  function handleSelectExisting(e) {
    const key = e.target.value;
    setSelectedKey(key);
    const found = options.find((o) => o.key === key);
    if (!found) {
      setPrefill(null);
      return;
    }
    const p = found.photo;
    const s512 = p.srcSet?.find((s) => (s.width || 0) === 512) || {};
    const s1024 = p.srcSet?.find((s) => (s.width || 0) === 1024) || {};
    const sOriginal = p.srcSet?.find((s) => (s.width || 0) > 1500) || {};
    setPrefill({
      src1024: p.src || "",
      width1024: p.width || 0,
      height1024: p.height || 0,
      description: p.description || "",
      alt: p.alt || "",
      src512: s512.src || "",
      width512: s512.width || 0,
      height512: s512.height || 0,
      srcOriginal: sOriginal.src || "",
      widthOriginal: sOriginal.width || 0,
      heightOriginal: sOriginal.height || 0,
      previousSrc: p.src || "",
    });
    setFormKey((k) => k + 1);
  }

  function handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const raw = Object.fromEntries(formData.entries());

    function toInt(value) {
      const v = parseInt(value, 10);
      return isNaN(v) ? 0 : v;
    }

    const photo = {
      src: raw.src1024 || "",
      width: toInt(raw.width1024),
      height: toInt(raw.height1024),
      description: raw.description || "",
      alt: raw.alt || "",
      srcSet: [
        {
          src: raw.src512 || "",
          width: toInt(raw.width512),
          height: toInt(raw.height512),
        },
        {
          src: raw.src1024 || "",
          width: toInt(raw.width1024),
          height: toInt(raw.height1024),
        },
      ],
    };
    if ((raw.srcOriginal || "").trim() !== "") {
      photo.srcSet.push({
        src: raw.srcOriginal.trim(),
        width: toInt(raw.widthOriginal),
        height: toInt(raw.heightOriginal),
      });
    }

    const payload = mode === "edit" ? { action: "update", previousSrc: prefill?.previousSrc || photo.src, photo } : photo;
    setOutputJson(payload);
  }

  const jsonString = outputJson ? JSON.stringify(outputJson, null, 2) : "";

  return (
    <div>
      <h3>Add a culture photo</h3>
      <Helmet>
        <title>Add a culture photo - Khoury Vis Lab</title>
        <meta name="description" content="Add a culture photo" />
      </Helmet>

      {/* Edit mode disabled per request; keep Add-only UI */}

      <form key={formKey} method="post" onSubmit={handleSubmit}>
        <hr />
        <h4>Required fields</h4>
        <p>
          <label>
            <strong>1024px image URL</strong>
            <br />
            <textarea name="src1024" rows={1} cols={120} defaultValue={prefill?.src1024 || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>1024px width</strong>
            <br />
            <textarea name="width1024" rows={1} cols={20} defaultValue={prefill?.width1024 || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>1024px height</strong>
            <br />
            <textarea name="height1024" rows={1} cols={20} defaultValue={prefill?.height1024 || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Description</strong>
            <br />
            <textarea name="description" rows={2} cols={120} defaultValue={prefill?.description || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Alt text</strong>
            <br />
            <textarea name="alt" rows={2} cols={120} defaultValue={prefill?.alt || ''} />
          </label>
        </p>

        <h4>Additional sizes</h4>
        <p>
          <label>
            <strong>512px image URL</strong>
            <br />
            <textarea name="src512" rows={1} cols={120} defaultValue={prefill?.src512 || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>512px width</strong>
            <br />
            <textarea name="width512" rows={1} cols={20} defaultValue={prefill?.width512 || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>512px height</strong>
            <br />
            <textarea name="height512" rows={1} cols={20} defaultValue={prefill?.height512 || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Original image URL</strong> (optional)
            <br />
            <textarea name="srcOriginal" rows={1} cols={120} defaultValue={prefill?.srcOriginal || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Original width</strong>
            <br />
            <textarea name="widthOriginal" rows={1} cols={20} defaultValue={prefill?.widthOriginal || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Original height</strong>
            <br />
            <textarea name="heightOriginal" rows={1} cols={20} defaultValue={prefill?.heightOriginal || ''} />
          </label>
        </p>

        <hr />
        <h4>Submitting</h4>
        <label>
          <input
            type="checkbox"
            defaultChecked={false}
            checked={agree}
            onChange={() => setAgree(!agree)}
          />{' '}
          I have filled required fields and checked for accuracy.
        </label>
        <Button type="submit" className="mt-3 d-block" disabled={!agree}>
          Step 1: Generate JSON
        </Button>
      </form>

      {outputJson && (
        <div>
          <Stack direction="horizontal" gap={3} className="mt-2">
            <Button onClick={() => copyToClipboard(jsonString)}>
              Step 2: Copy to clipboard
            </Button>
            <Button
              className="mt-2"
              onClick={() => {
                const isEdit = mode === 'edit';
                const baseUrl = isEdit
                  ? "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=update_culture&projects=&title=[UPDATE CULTURE PHOTO] "
                  : "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=new_culture&projects=&template=new-culture-template.md&title=[NEW CULTURE PHOTO] ";
                const url = baseUrl + encodeURIComponent("photo");
                window.open(url);
              }}
            >
              Step 3: Paste into new issue
            </Button>
          </Stack>
          <pre>{jsonString}</pre>
        </div>
      )}
    </div>
  );
};

const Culture = ({ culture }) => {
  let { path } = useRouteMatch();
  return (
    <Switch>
      <Route exact path={path}>
        <CultureGallery culture={culture} />
      </Route>
      <Route path={`${path}/add`}>
        <AddEditCulture />
      </Route>
    </Switch>
  );
};

export default Culture;
